const expandKeyedTranslations = (groups) => {
  const translations = {}

  groups.forEach((group) => {
    Object.entries(group).forEach(([phrase, byLanguage]) => {
      Object.entries(byLanguage).forEach(([language, translation]) => {
        translations[language] = translations[language] || {}
        translations[language][phrase] = translation
      })
    })
  })

  return translations
}

export const composableDirectionTranslations = {
  fr: { left: 'gauche', right: 'droite', forward: 'avant' },
  de: { left: 'links', right: 'rechts', forward: 'vorwärts' },
  es: { left: 'izquierda', right: 'derecha', forward: 'adelante' },
  pt: { left: 'esquerda', right: 'direita', forward: 'frente' },
  zh_CN: { left: '左', right: '右', forward: '前' },
  zh_TW: { left: '左', right: '右', forward: '前' },
  it: { left: 'sinistra', right: 'destra', forward: 'avanti' },
  ko: { left: '왼쪽', right: '오른쪽', forward: '앞' },
  nl: { left: 'links', right: 'rechts', forward: 'vooruit' },
  fi: { left: 'vasemmalle', right: 'oikealle', forward: 'eteen' },
  ru: { left: 'влево', right: 'вправо', forward: 'вперёд' },
  sv: { left: 'vänster', right: 'höger', forward: 'framåt' },
  ja: { left: '左', right: '右', forward: '前' },
  tr: { left: 'sol', right: 'sağ', forward: 'ileri' },
  cs: { left: 'doleva', right: 'doprava', forward: 'dopředu' },
  el: { left: 'αριστερά', right: 'δεξιά', forward: 'μπροστά' },
  hr: { left: 'lijevo', right: 'desno', forward: 'naprijed' },
}

const reportLabels = {
  Additional: {
    fr: 'Supplémentaire',
    de: 'Zusätzlich',
    es: 'Adicional',
    pt: 'Adicional',
    zh_CN: '其他',
    zh_TW: '其他',
    it: 'Metriche aggiuntive',
    ko: '추가',
    nl: 'Aanvullend',
    fi: 'Lisä',
    ru: 'Дополнительно',
    sv: 'Ytterligare',
    ja: 'その他',
    tr: 'Ek',
    cs: 'Další',
    el: 'Πρόσθετα',
    hr: 'Dodatno',
  },
  'Back Left': {
    fr: 'Arrière gauche',
    de: 'Hinten links',
    es: 'Posterior izquierda',
    pt: 'Traseira esquerda',
    zh_CN: '后左',
    zh_TW: '後左',
    it: 'Posteriore sinistra',
    ko: '뒤 왼쪽',
    nl: 'Achter links',
    fi: 'Takana vasen',
    ru: 'Сзади слева',
    sv: 'Bak vänster',
    ja: '後ろ左',
    tr: 'Arka sol',
    cs: 'Vzadu vlevo',
    el: 'Πίσω αριστερά',
    hr: 'Straga lijevo',
  },
  'Back Right': {
    fr: 'Arrière droit',
    de: 'Hinten rechts',
    es: 'Posterior derecha',
    pt: 'Traseira direita',
    zh_CN: '后右',
    zh_TW: '後右',
    it: 'Posteriore destra',
    ko: '뒤 오른쪽',
    nl: 'Achter rechts',
    fi: 'Takana oikea',
    ru: 'Сзади справа',
    sv: 'Bak höger',
    ja: '後ろ右',
    tr: 'Arka sağ',
    cs: 'Vzadu vpravo',
    el: 'Πίσω δεξιά',
    hr: 'Straga desno',
  },
  'Basal Metabolic Rate (BMR)': {
    fr: 'Métabolisme de base (BMR)',
    de: 'Grundumsatz (BMR)',
    es: 'Tasa metabólica basal (BMR)',
    pt: 'Taxa metabólica basal (BMR)',
    zh_CN: '基础代谢率 (BMR)',
    zh_TW: '基礎代謝率 (BMR)',
    it: 'Metabolismo basale (BMR)',
    ko: '기초대사량 (BMR)',
    nl: 'Basale stofwisseling (BMR)',
    fi: 'Perusaineenvaihdunta (BMR)',
    ru: 'Базовый метаболизм (BMR)',
    sv: 'Basalmetabolism (BMR)',
    ja: '基礎代謝量 (BMR)',
    tr: 'Bazal metabolizma hızı (BMR)',
    cs: 'Bazální metabolismus (BMR)',
    el: 'Βασικός μεταβολικός ρυθμός (BMR)',
    hr: 'Bazalni metabolizam (BMR)',
  },
  Biceps: {
    fr: 'Biceps',
    de: 'Bizeps',
    es: 'Bíceps',
    pt: 'Bíceps',
    zh_CN: '肱二头肌',
    zh_TW: '肱二頭肌',
    it: 'Bicipiti',
    ko: '이두근',
    nl: 'Biceps',
    fi: 'Hauis',
    ru: 'Бицепс',
    sv: 'Biceps',
    ja: '上腕二頭筋',
    tr: 'Biceps',
    cs: 'Biceps',
    el: 'Δικέφαλος',
    hr: 'Biceps',
  },
  'Blood Pressure': {
    fr: 'Pression artérielle',
    de: 'Blutdruck',
    es: 'Presión arterial',
    pt: 'Pressão arterial',
    zh_CN: '血压',
    zh_TW: '血壓',
    it: 'Pressione sanguigna',
    ko: '혈압',
    nl: 'Bloeddruk',
    fi: 'Verenpaine',
    ru: 'Артериальное давление',
    sv: 'Blodtryck',
    ja: '血圧',
    tr: 'Kan basıncı',
    cs: 'Krevní tlak',
    el: 'Αρτηριακή πίεση',
    hr: 'Krvni tlak',
  },
  'Body Fat Percent': {
    fr: 'Pourcentage de graisse corporelle',
    de: 'Körperfettanteil',
    es: 'Porcentaje de grasa corporal',
    pt: 'Percentual de gordura corporal',
    zh_CN: '体脂百分比',
    zh_TW: '體脂百分比',
    it: 'Percentuale di grasso corporeo',
    ko: '체지방률',
    nl: 'Lichaamsvetpercentage',
    fi: 'Kehon rasvaprosentti',
    ru: 'Процент жира в теле',
    sv: 'Kroppsfettprocent',
    ja: '体脂肪率',
    tr: 'Vücut yağ yüzdesi',
    cs: 'Procento tělesného tuku',
    el: 'Ποσοστό σωματικού λίπους',
    hr: 'Postotak tjelesne masti',
  },
  'Body Fat Percentage': {
    fr: 'Pourcentage de graisse corporelle',
    de: 'Körperfettanteil',
    es: 'Porcentaje de grasa corporal',
    pt: 'Percentual de gordura corporal',
    zh_CN: '体脂百分比',
    zh_TW: '體脂百分比',
    it: 'Percentuale di grasso corporeo',
    ko: '체지방률',
    nl: 'Lichaamsvetpercentage',
    fi: 'Kehon rasvaprosentti',
    ru: 'Процент жира в теле',
    sv: 'Kroppsfettprocent',
    ja: '体脂肪率',
    tr: 'Vücut yağ yüzdesi',
    cs: 'Procento tělesného tuku',
    el: 'Ποσοστό σωματικού λίπους',
    hr: 'Postotak tjelesne masti',
  },
  'Body Fat Percentage (BFP)': {
    fr: 'Pourcentage de graisse corporelle (BFP)',
    de: 'Körperfettanteil (BFP)',
    es: 'Porcentaje de grasa corporal (BFP)',
    pt: 'Percentual de gordura corporal (BFP)',
    zh_CN: '体脂百分比 (BFP)',
    zh_TW: '體脂百分比 (BFP)',
    it: 'Percentuale di grasso corporeo (BFP)',
    ko: '체지방률 (BFP)',
    nl: 'Lichaamsvetpercentage (BFP)',
    fi: 'Kehon rasvaprosentti (BFP)',
    ru: 'Процент жира в теле (BFP)',
    sv: 'Kroppsfettprocent (BFP)',
    ja: '体脂肪率 (BFP)',
    tr: 'Vücut yağ yüzdesi (BFP)',
    cs: 'Procento tělesného tuku (BFP)',
    el: 'Ποσοστό σωματικού λίπους (BFP)',
    hr: 'Postotak tjelesne masti (BFP)',
  },
  Bust: {
    fr: 'Buste',
    de: 'Büste',
    es: 'Busto',
    pt: 'Busto',
    zh_CN: '胸围',
    zh_TW: '胸圍',
    it: 'Busto',
    ko: '가슴',
    nl: 'Buste',
    fi: 'Rintakehä',
    ru: 'Бюст',
    sv: 'Byst',
    ja: 'バスト',
    tr: 'Göğüs',
    cs: 'Poprsí',
    el: 'Στήθος',
    hr: 'Prsa',
  },
  Chest: {
    fr: 'Poitrine',
    de: 'Brust',
    es: 'Pecho',
    pt: 'Peito',
    zh_CN: '胸部',
    zh_TW: '胸部',
    it: 'Torace',
    ko: '가슴',
    nl: 'Borst',
    fi: 'Rinta',
    ru: 'Грудь',
    sv: 'Bröst',
    ja: '胸',
    tr: 'Göğüs',
    cs: 'Hruď',
    el: 'Στήθος',
    hr: 'Prsa',
  },
  'Demo Location Guide': {
    fr: 'Guide des emplacements de mesure',
    de: 'Positionsanleitung',
    es: 'Guía de ubicación',
    pt: 'Guia de localização',
    zh_CN: '测量位置指南',
    zh_TW: '測量位置指南',
    it: 'Guida alle posizioni',
    ko: '측정 위치 안내',
    nl: 'Locatiegids',
    fi: 'Mittauspaikkaopas',
    ru: 'Схема положений',
    sv: 'Positionsguide',
    ja: '測定位置ガイド',
    tr: 'Konum kılavuzu',
    cs: 'Průvodce umístěním',
    el: 'Οδηγός θέσεων',
    hr: 'Vodič položaja',
  },
  'Demo Location Guide (Sample Image)*': {
    fr: "Guide des emplacements de mesure (image d'exemple)*",
    de: 'Positionsanleitung (Beispielbild)*',
    es: 'Guía de ubicación (imagen de ejemplo)*',
    pt: 'Guia de localização (imagem de exemplo)*',
    zh_CN: '测量位置指南（示例图）*',
    zh_TW: '測量位置指南（示例圖）*',
    it: 'Guida alle posizioni (immagine di esempio)*',
    ko: '측정 위치 안내 (샘플 이미지)*',
    nl: 'Locatiegids (voorbeeldafbeelding)*',
    fi: 'Mittauspaikkaopas (esimerkkikuva)*',
    ru: 'Схема положений (пример изображения)*',
    sv: 'Positionsguide (exempelbild)*',
    ja: '測定位置ガイド（サンプル画像）*',
    tr: 'Konum kılavuzu (örnek görüntü)*',
    cs: 'Průvodce umístěním (ukázkový obrázek)*',
    el: 'Οδηγός θέσεων (δείγμα εικόνας)*',
    hr: 'Vodič položaja (ogledna slika)*',
  },
  'Fat Mass': {
    fr: 'Masse grasse',
    de: 'Fettmasse',
    es: 'Masa grasa',
    pt: 'Massa gorda',
    zh_CN: '脂肪量',
    zh_TW: '脂肪量',
    it: 'Massa grassa',
    ko: '지방량',
    nl: 'Vetmassa',
    fi: 'Rasvamassa',
    ru: 'Жировая масса',
    sv: 'Fettmassa',
    ja: '脂肪量',
    tr: 'Yağ kütlesi',
    cs: 'Tuková hmota',
    el: 'Λιπώδης μάζα',
    hr: 'Masna masa',
  },
  'Front Left': {
    fr: 'Avant gauche',
    de: 'Vorne links',
    es: 'Anterior izquierda',
    pt: 'Frontal esquerda',
    zh_CN: '前左',
    zh_TW: '前左',
    it: 'Anteriore sinistra',
    ko: '앞 왼쪽',
    nl: 'Voor links',
    fi: 'Edessä vasen',
    ru: 'Спереди слева',
    sv: 'Fram vänster',
    ja: '前左',
    tr: 'Ön sol',
    cs: 'Vpředu vlevo',
    el: 'Μπροστά αριστερά',
    hr: 'Sprijeda lijevo',
  },
  'Front Right': {
    fr: 'Avant droit',
    de: 'Vorne rechts',
    es: 'Anterior derecha',
    pt: 'Frontal direita',
    zh_CN: '前右',
    zh_TW: '前右',
    it: 'Anteriore destra',
    ko: '앞 오른쪽',
    nl: 'Voor rechts',
    fi: 'Edessä oikea',
    ru: 'Спереди справа',
    sv: 'Fram höger',
    ja: '前右',
    tr: 'Ön sağ',
    cs: 'Vpředu vpravo',
    el: 'Μπροστά δεξιά',
    hr: 'Sprijeda desno',
  },
  'Front/Back': {
    fr: 'Avant/arrière',
    de: 'Vorne/hinten',
    es: 'Frente/espalda',
    pt: 'Frente/costas',
    zh_CN: '前/后',
    zh_TW: '前/後',
    it: 'Vista anteriore/posteriore',
    ko: '앞/뒤',
    nl: 'Voor/achter',
    fi: 'Etu/taka',
    ru: 'Спереди/сзади',
    sv: 'Fram/bak',
    ja: '前/後',
    tr: 'Ön/arka',
    cs: 'Přední/zadní',
    el: 'Εμπρός/πίσω',
    hr: 'Naprijed/natrag',
  },
  Head: {
    fr: 'Tête',
    de: 'Kopf',
    es: 'Cabeza',
    pt: 'Cabeça',
    zh_CN: '头',
    zh_TW: '頭',
    it: 'Testa',
    ko: '머리',
    nl: 'Hoofd',
    fi: 'Pää',
    ru: 'Голова',
    sv: 'Huvud',
    ja: '頭',
    tr: 'Baş',
    cs: 'Hlava',
    el: 'Κεφάλι',
    hr: 'Glava',
  },
  'Heart Rate': {
    fr: 'Fréquence cardiaque',
    de: 'Herzfrequenz',
    es: 'Frecuencia cardíaca',
    pt: 'Frequência cardíaca',
    zh_CN: '心率',
    zh_TW: '心率',
    it: 'Frequenza cardiaca',
    ko: '심박수',
    nl: 'Hartslag',
    fi: 'Syke',
    ru: 'Частота сердечных сокращений',
    sv: 'Hjärtfrekvens',
    ja: '心拍数',
    tr: 'Kalp atış hızı',
    cs: 'Srdeční frekvence',
    el: 'Καρδιακός ρυθμός',
    hr: 'Otkucaji srca',
  },
  Hip: {
    fr: 'Hanche',
    de: 'Hüfte',
    es: 'Cadera',
    pt: 'Anca',
    zh_CN: '髋',
    zh_TW: '髖',
    it: 'Anca',
    ko: '엉덩이',
    nl: 'Heup',
    fi: 'Lantio',
    ru: 'Бедро',
    sv: 'Höft',
    ja: '腰',
    tr: 'Kalça',
    cs: 'Kyčel',
    el: 'Ισχίο',
    hr: 'Kuk',
  },
  'Hip Circumference': {
    fr: 'Tour de hanches',
    de: 'Hüftumfang',
    es: 'Circunferencia de cadera',
    pt: 'Circunferência do quadril',
    zh_CN: '臀围',
    zh_TW: '臀圍',
    it: 'Circonferenza fianchi',
    ko: '엉덩이둘레',
    nl: 'Heupomtrek',
    fi: 'Lantionympärys',
    ru: 'Обхват бёдер',
    sv: 'Höftomfång',
    ja: 'ヒップ周囲',
    tr: 'Kalça çevresi',
    cs: 'Obvod boků',
    el: 'Περίμετρος ισχίων',
    hr: 'Opseg bokova',
  },
  Hips: {
    fr: 'Hanches',
    de: 'Hüften',
    es: 'Caderas',
    pt: 'Quadris',
    zh_CN: '臀部',
    zh_TW: '臀部',
    it: 'Fianchi',
    ko: '엉덩이',
    nl: 'Heupen',
    fi: 'Lantio',
    ru: 'Бёдра',
    sv: 'Höfter',
    ja: 'ヒップ',
    tr: 'Kalçalar',
    cs: 'Boky',
    el: 'Ισχία',
    hr: 'Bokovi',
  },
  'How do I compare to other Fit3D users?': {
    fr: 'Comment je me compare aux autres utilisateurs Fit3D ?',
    de: 'Wie schneide ich im Vergleich zu anderen Fit3D-Nutzern ab?',
    es: '¿Cómo me comparo con otros usuarios de Fit3D?',
    pt: 'Como me comparo com outros usuários Fit3D?',
    zh_CN: '我与其他 Fit3D 用户相比如何？',
    zh_TW: '我與其他 Fit3D 使用者相比如何？',
    it: 'Come mi confronto con gli altri utenti Fit3D?',
    ko: '다른 Fit3D 사용자와 비교하면 어떤가요?',
    nl: 'Hoe vergelijk ik met andere Fit3D-gebruikers?',
    fi: 'Miten vertaudun muihin Fit3D-käyttäjiin?',
    ru: 'Как я выгляжу на фоне других пользователей Fit3D?',
    sv: 'Hur jämför jag med andra Fit3D-användare?',
    ja: '他の Fit3D ユーザーと比べてどうですか？',
    tr: 'Diğer Fit3D kullanıcılarıyla nasıl karşılaştırılırım?',
    cs: 'Jak si stojím ve srovnání s ostatními uživateli Fit3D?',
    el: 'Πώς συγκρίνομαι με άλλους χρήστες Fit3D;',
    hr: 'Kako se uspoređujem s ostalim Fit3D korisnicima?',
  },
  Knee: {
    fr: 'Genou',
    de: 'Knie',
    es: 'Rodilla',
    pt: 'Joelho',
    zh_CN: '膝',
    zh_TW: '膝',
    it: 'Ginocchio',
    ko: '무릎',
    nl: 'Knie',
    fi: 'Polvi',
    ru: 'Колено',
    sv: 'Knä',
    ja: '膝',
    tr: 'Diz',
    cs: 'Koleno',
    el: 'Γόνατο',
    hr: 'Koljeno',
  },
  'Lean Mass': {
    fr: 'Masse maigre',
    de: 'Magermasse',
    es: 'Masa magra',
    pt: 'Massa magra',
    zh_CN: '瘦体重',
    zh_TW: '瘦體重',
    it: 'Massa magra',
    ko: '제지방량',
    nl: 'Magere massa',
    fi: 'Rasvaton massa',
    ru: 'Тощая масса',
    sv: 'Fettfri massa',
    ja: '除脂肪量',
    tr: 'Yağsız kütle',
    cs: 'Hubená hmota',
    el: 'Άλιπη μάζα',
    hr: 'Mršava masa',
  },
  'Left Biceps': {
    fr: 'Biceps gauche',
    de: 'Linker Bizeps',
    es: 'Bíceps izquierdo',
    pt: 'Bíceps esquerdo',
    zh_CN: '左肱二头肌',
    zh_TW: '左肱二頭肌',
    it: 'Bicipite sinistro',
    ko: '왼쪽 이두근',
    nl: 'Linker biceps',
    fi: 'Vasen hauis',
    ru: 'Левый бицепс',
    sv: 'Vänster biceps',
    ja: '左上腕二頭筋',
    tr: 'Sol biceps',
    cs: 'Levý biceps',
    el: 'Αριστερός δικέφαλος',
    hr: 'Lijevi biceps',
  },
  'Left Calf': {
    fr: 'Mollet gauche',
    de: 'Linke Wade',
    es: 'Pantorrilla izquierda',
    pt: 'Panturrilha esquerda',
    zh_CN: '左小腿',
    zh_TW: '左小腿',
    it: 'Polpaccio sinistro',
    ko: '왼쪽 종아리',
    nl: 'Linker kuit',
    fi: 'Vasen pohje',
    ru: 'Левая икра',
    sv: 'Vänster vad',
    ja: '左ふくらはぎ',
    tr: 'Sol baldır',
    cs: 'Levé lýtko',
    el: 'Αριστερή γάμπα',
    hr: 'Lijeva potkoljenica',
  },
  'Left Forearm': {
    fr: 'Avant-bras gauche',
    de: 'Linker Unterarm',
    es: 'Antebrazo izquierdo',
    pt: 'Antebraço esquerdo',
    zh_CN: '左前臂',
    zh_TW: '左前臂',
    it: 'Avambraccio sinistro',
    ko: '왼쪽 전완',
    nl: 'Linker onderarm',
    fi: 'Vasen kyynärvarsi',
    ru: 'Левое предплечье',
    sv: 'Vänster underarm',
    ja: '左前腕',
    tr: 'Sol ön kol',
    cs: 'Levé předloktí',
    el: 'Αριστερός πήχης',
    hr: 'Lijeva podlaktica',
  },
  'Left Thigh': {
    fr: 'Cuisse gauche',
    de: 'Linker Oberschenkel',
    es: 'Muslo izquierdo',
    pt: 'Coxa esquerda',
    zh_CN: '左大腿',
    zh_TW: '左大腿',
    it: 'Coscia sinistra',
    ko: '왼쪽 허벅지',
    nl: 'Linker dij',
    fi: 'Vasen reisi',
    ru: 'Левое бедро',
    sv: 'Vänster lår',
    ja: '左太もも',
    tr: 'Sol uyluk',
    cs: 'Levé stehno',
    el: 'Αριστερός μηρός',
    hr: 'Lijevo bedro',
  },
  Location: {
    fr: 'Emplacement',
    de: 'Position',
    es: 'Ubicación',
    pt: 'Localização',
    zh_CN: '位置',
    zh_TW: '位置',
    it: 'Posizione',
    ko: '위치',
    nl: 'Locatie',
    fi: 'Sijainti',
    ru: 'Расположение',
    sv: 'Plats',
    ja: '位置',
    tr: 'Konum',
    cs: 'Umístění',
    el: 'Θέση',
    hr: 'Položaj',
  },
  Measurement: {
    fr: 'Mesure',
    de: 'Messung',
    es: 'Medida',
    pt: 'Medida',
    zh_CN: '测量',
    zh_TW: '測量',
    it: 'Misura',
    ko: '측정',
    nl: 'Meting',
    fi: 'Mittaus',
    ru: 'Измерение',
    sv: 'Mått',
    ja: '測定',
    tr: 'Ölçüm',
    cs: 'Měření',
    el: 'Μέτρηση',
    hr: 'Mjera',
  },
  'Measurement location guide': {
    fr: 'Guide des emplacements de mesure',
    de: 'Anleitung zu den Messpositionen',
    es: 'Guía de ubicación de las medidas',
    pt: 'Guia de localização das medidas',
    zh_CN: '测量位置指南',
    zh_TW: '測量位置指南',
    it: 'Guida alle posizioni delle misure',
    ko: '측정 위치 안내',
    nl: 'Gids voor meetlocaties',
    fi: 'Mittauspaikkaopas',
    ru: 'Схема положений измерений',
    sv: 'Guide till mätpositioner',
    ja: '測定位置ガイド',
    tr: 'Ölçüm konumu kılavuzu',
    cs: 'Průvodce umístěním měření',
    el: 'Οδηγός θέσεων μέτρησης',
    hr: 'Vodič položaja mjerenja',
  },
  Medical: {
    fr: 'Médical',
    de: 'Medizinisch',
    es: 'Médico',
    pt: 'Médico',
    zh_CN: '医学',
    zh_TW: '醫學',
    it: 'Medico',
    ko: '의료',
    nl: 'Medisch',
    fi: 'Lääketieteellinen',
    ru: 'Медицина',
    sv: 'Medicinskt',
    ja: '医療',
    tr: 'Tıbbi',
    cs: 'Lékařské',
    el: 'Ιατρικά',
    hr: 'Medicinsko',
  },
  Neck: {
    fr: 'Cou',
    de: 'Hals',
    es: 'Cuello',
    pt: 'Pescoço',
    zh_CN: '颈部',
    zh_TW: '頸部',
    it: 'Collo',
    ko: '목',
    nl: 'Nek',
    fi: 'Kaula',
    ru: 'Шея',
    sv: 'Nacke',
    ja: '首',
    tr: 'Boyun',
    cs: 'Krk',
    el: 'Λαιμός',
    hr: 'Vrat',
  },
  'Percent (%)': {
    fr: 'Pourcentage (%)',
    de: 'Prozent (%)',
    es: 'Porcentaje (%)',
    pt: 'Percentual (%)',
    zh_CN: '百分比 (%)',
    zh_TW: '百分比 (%)',
    it: 'Percentuale (%)',
    ko: '백분율 (%)',
    nl: 'Percentage (%)',
    fi: 'Prosentti (%)',
    ru: 'Процент (%)',
    sv: 'Procent (%)',
    ja: 'パーセント (%)',
    tr: 'Yüzde (%)',
    cs: 'Procento (%)',
    el: 'Ποσοστό (%)',
    hr: 'Postotak (%)',
  },
  'Research Description': {
    fr: 'Description de la recherche',
    de: 'Forschungsbeschreibung',
    es: 'Descripción de la investigación',
    pt: 'Descrição da pesquisa',
    zh_CN: '研究说明',
    zh_TW: '研究說明',
    it: 'Descrizione della ricerca',
    ko: '연구 설명',
    nl: 'Onderzoeksbeschrijving',
    fi: 'Tutkimuksen kuvaus',
    ru: 'Описание исследования',
    sv: 'Forskningsbeskrivning',
    ja: '研究の説明',
    tr: 'Araştırma açıklaması',
    cs: 'Popis výzkumu',
    el: 'Περιγραφή έρευνας',
    hr: 'Opis istraživanja',
  },
  'Return to Top': {
    fr: 'Retour en haut',
    de: 'Nach oben',
    es: 'Volver arriba',
    pt: 'Voltar ao topo',
    zh_CN: '返回顶部',
    zh_TW: '返回頂部',
    it: 'Torna su',
    ko: '맨 위로',
    nl: 'Terug naar boven',
    fi: 'Takaisin ylös',
    ru: 'Наверх',
    sv: 'Tillbaka till toppen',
    ja: '先頭に戻る',
    tr: 'Yukarı dön',
    cs: 'Zpět nahoru',
    el: 'Επιστροφή στην κορυφή',
    hr: 'Povratak na vrh',
  },
  'Right Biceps': {
    fr: 'Biceps droit',
    de: 'Rechter Bizeps',
    es: 'Bíceps derecho',
    pt: 'Bíceps direito',
    zh_CN: '右肱二头肌',
    zh_TW: '右肱二頭肌',
    it: 'Bicipite destro',
    ko: '오른쪽 이두근',
    nl: 'Rechter biceps',
    fi: 'Oikea hauis',
    ru: 'Правый бицепс',
    sv: 'Höger biceps',
    ja: '右上腕二頭筋',
    tr: 'Sağ biceps',
    cs: 'Pravý biceps',
    el: 'Δεξιός δικέφαλος',
    hr: 'Desni biceps',
  },
  'Right Calf': {
    fr: 'Mollet droit',
    de: 'Rechte Wade',
    es: 'Pantorrilla derecha',
    pt: 'Panturrilha direita',
    zh_CN: '右小腿',
    zh_TW: '右小腿',
    it: 'Polpaccio destro',
    ko: '오른쪽 종아리',
    nl: 'Rechter kuit',
    fi: 'Oikea pohje',
    ru: 'Правая икра',
    sv: 'Höger vad',
    ja: '右ふくらはぎ',
    tr: 'Sağ baldır',
    cs: 'Pravé lýtko',
    el: 'Δεξιά γάμπα',
    hr: 'Desna potkoljenica',
  },
  'Right Forearm': {
    fr: 'Avant-bras droit',
    de: 'Rechter Unterarm',
    es: 'Antebrazo derecho',
    pt: 'Antebraço direito',
    zh_CN: '右前臂',
    zh_TW: '右前臂',
    it: 'Avambraccio destro',
    ko: '오른쪽 전완',
    nl: 'Rechter onderarm',
    fi: 'Oikea kyynärvarsi',
    ru: 'Правое предплечье',
    sv: 'Höger underarm',
    ja: '右前腕',
    tr: 'Sağ ön kol',
    cs: 'Pravé předloktí',
    el: 'Δεξιός πήχης',
    hr: 'Desna podlaktica',
  },
  'Right Thigh': {
    fr: 'Cuisse droite',
    de: 'Rechter Oberschenkel',
    es: 'Muslo derecho',
    pt: 'Coxa direita',
    zh_CN: '右大腿',
    zh_TW: '右大腿',
    it: 'Coscia destra',
    ko: '오른쪽 허벅지',
    nl: 'Rechter dij',
    fi: 'Oikea reisi',
    ru: 'Правое бедро',
    sv: 'Höger lår',
    ja: '右太もも',
    tr: 'Sağ uyluk',
    cs: 'Pravé stehno',
    el: 'Δεξιός μηρός',
    hr: 'Desno bedro',
  },
  Shoulder: {
    fr: 'Épaule',
    de: 'Schulter',
    es: 'Hombro',
    pt: 'Ombro',
    zh_CN: '肩',
    zh_TW: '肩',
    it: 'Spalla',
    ko: '어깨',
    nl: 'Schouder',
    fi: 'Olkapää',
    ru: 'Плечо',
    sv: 'Skuldra',
    ja: '肩',
    tr: 'Omuz',
    cs: 'Rameno',
    el: 'Ώμος',
    hr: 'Rame',
  },
  'Shift (cm)': {
    fr: 'Décalage (cm)',
    de: 'Verschiebung (cm)',
    es: 'Desplazamiento (cm)',
    pt: 'Deslocamento (cm)',
    zh_CN: '偏移 (cm)',
    zh_TW: '偏移 (cm)',
    it: 'Spostamento (cm)',
    ko: '이동 (cm)',
    nl: 'Verschuiving (cm)',
    fi: 'Siirtymä (cm)',
    ru: 'Смещение (см)',
    sv: 'Förskjutning (cm)',
    ja: 'ずれ (cm)',
    tr: 'Kayma (cm)',
    cs: 'Posun (cm)',
    el: 'Μετατόπιση (cm)',
    hr: 'Pomak (cm)',
  },
  'Shift (Inches)': {
    fr: 'Décalage (pouces)',
    de: 'Verschiebung (Zoll)',
    es: 'Desplazamiento (pulgadas)',
    pt: 'Deslocamento (polegadas)',
    zh_CN: '偏移（英寸）',
    zh_TW: '偏移（英吋）',
    it: 'Spostamento (pollici)',
    ko: '이동 (인치)',
    nl: 'Verschuiving (inches)',
    fi: 'Siirtymä (tuumaa)',
    ru: 'Смещение (дюймы)',
    sv: 'Förskjutning (tum)',
    ja: 'ずれ (インチ)',
    tr: 'Kayma (inç)',
    cs: 'Posun (palce)',
    el: 'Μετατόπιση (ίντσες)',
    hr: 'Pomak (inči)',
  },
  Side: {
    fr: 'Vue de profil',
    de: 'Seitenansicht',
    es: 'Vista lateral',
    pt: 'Vista lateral',
    zh_CN: '侧面',
    zh_TW: '側面',
    it: 'Vista laterale',
    ko: '측면',
    nl: 'Zijaanzicht',
    fi: 'Sivu',
    ru: 'Вид сбоку',
    sv: 'Sida',
    ja: '側面',
    tr: 'Yan',
    cs: 'Bok',
    el: 'Πλάγια',
    hr: 'Sa strane',
  },
  Thigh: {
    fr: 'Cuisse',
    de: 'Oberschenkel',
    es: 'Muslo',
    pt: 'Coxa',
    zh_CN: '大腿',
    zh_TW: '大腿',
    it: 'Coscia',
    ko: '허벅지',
    nl: 'Dij',
    fi: 'Reisi',
    ru: 'Бедро',
    sv: 'Lår',
    ja: '太もも',
    tr: 'Uyluk',
    cs: 'Stehno',
    el: 'Μηρός',
    hr: 'Bedro',
  },
  'Tilt (%)': {
    fr: 'Inclinaison (%)',
    de: 'Neigung (%)',
    es: 'Inclinación (%)',
    pt: 'Inclinação (%)',
    zh_CN: '倾斜 (%)',
    zh_TW: '傾斜 (%)',
    it: 'Inclinazione (%)',
    ko: '기울기 (%)',
    nl: 'Kanteling (%)',
    fi: 'Kallistus (%)',
    ru: 'Наклон (%)',
    sv: 'Lutning (%)',
    ja: '傾き (%)',
    tr: 'Eğim (%)',
    cs: 'Náklon (%)',
    el: 'Κλίση (%)',
    hr: 'Nagib (%)',
  },
  'Trunk to Leg': {
    fr: 'Tronc vers jambes',
    de: 'Rumpf zu Bein',
    es: 'Tronco a pierna',
    pt: 'Tronco para perna',
    zh_CN: '躯干与腿',
    zh_TW: '軀幹與腿',
    it: 'Tronco e gambe',
    ko: '몸통-다리',
    nl: 'Romp tot been',
    fi: 'Vartalo ja jalat',
    ru: 'Туловище и ноги',
    sv: 'Bål till ben',
    ja: '体幹と脚',
    tr: 'Gövde-bacak',
    cs: 'Trup k nohám',
    el: 'Κορμός προς πόδια',
    hr: 'Trup prema nogama',
  },
  Underbust: {
    fr: 'Sous-poitrine',
    de: 'Unterbrust',
    es: 'Bajo busto',
    pt: 'Abaixo do busto',
    zh_CN: '胸下围',
    zh_TW: '胸下圍',
    it: 'Sottoseno',
    ko: '가슴 아래',
    nl: 'Onderborst',
    fi: 'Rinnan alta',
    ru: 'Под грудью',
    sv: 'Under byst',
    ja: 'アンダーバスト',
    tr: 'Göğüs altı',
    cs: 'Pod prsy',
    el: 'Κάτω από το στήθος',
    hr: 'Ispod prsa',
  },
  Value: {
    fr: 'Valeur',
    de: 'Wert',
    es: 'Valor',
    pt: 'Valor',
    zh_CN: '数值',
    zh_TW: '數值',
    it: 'Valore',
    ko: '값',
    nl: 'Waarde',
    fi: 'Arvo',
    ru: 'Значение',
    sv: 'Värde',
    ja: '値',
    tr: 'Değer',
    cs: 'Hodnota',
    el: 'Τιμή',
    hr: 'Vrijednost',
  },
  '(Inches)': {
    fr: '(pouces)',
    de: '(Zoll)',
    es: '(pulgadas)',
    pt: '(polegadas)',
    zh_CN: '(英寸)',
    zh_TW: '(英吋)',
    it: '(pollici)',
    ko: '(인치)',
    nl: '(inches)',
    fi: '(tuumaa)',
    ru: '(дюймы)',
    sv: '(tum)',
    ja: '(インチ)',
    tr: '(inç)',
    cs: '(palce)',
    el: '(ίντσες)',
    hr: '(inči)',
  },
  'Value (cm)': {
    fr: 'Valeur (cm)',
    de: 'Wert (cm)',
    es: 'Valor (cm)',
    pt: 'Valor (cm)',
    zh_CN: '数值 (cm)',
    zh_TW: '數值 (cm)',
    it: 'Valore (cm)',
    ko: '값 (cm)',
    nl: 'Waarde (cm)',
    fi: 'Arvo (cm)',
    ru: 'Значение (см)',
    sv: 'Värde (cm)',
    ja: '値 (cm)',
    tr: 'Değer (cm)',
    cs: 'Hodnota (cm)',
    el: 'Τιμή (cm)',
    hr: 'Vrijednost (cm)',
  },
  'Value (Inches)': {
    fr: 'Valeur (pouces)',
    de: 'Wert (Zoll)',
    es: 'Valor (pulgadas)',
    pt: 'Valor (polegadas)',
    zh_CN: '数值（英寸）',
    zh_TW: '數值（英吋）',
    it: 'Valore (pollici)',
    ko: '값 (인치)',
    nl: 'Waarde (inches)',
    fi: 'Arvo (tuumaa)',
    ru: 'Значение (дюймы)',
    sv: 'Värde (tum)',
    ja: '値 (インチ)',
    tr: 'Değer (inç)',
    cs: 'Hodnota (palce)',
    el: 'Τιμή (ίντσες)',
    hr: 'Vrijednost (inči)',
  },
  'Value Inches': {
    fr: 'Valeur en pouces',
    de: 'Wert in Zoll',
    es: 'Valor en pulgadas',
    pt: 'Valor em polegadas',
    zh_CN: '英寸数值',
    zh_TW: '英吋數值',
    it: 'Valore in pollici',
    ko: '인치 값',
    nl: 'Waarde in inches',
    fi: 'Arvo tuumina',
    ru: 'Значение в дюймах',
    sv: 'Värde i tum',
    ja: 'インチ値',
    tr: 'İnç değeri',
    cs: 'Hodnota v palcích',
    el: 'Τιμή σε ίντσες',
    hr: 'Vrijednost u inčima',
  },
  'Wellness Metric': {
    fr: 'Métrique de bien-être',
    de: 'Wellness-Metrik',
    es: 'Métrica de bienestar',
    pt: 'Métrica de bem-estar',
    zh_CN: '健康指标',
    zh_TW: '健康指標',
    it: 'Metrica di benessere',
    ko: '웰니스 지표',
    nl: 'Wellnessmetriek',
    fi: 'Hyvinvointimittari',
    ru: 'Показатель здоровья',
    sv: 'Wellnessmått',
    ja: 'ウェルネス指標',
    tr: 'Wellness metriği',
    cs: 'Wellness metrika',
    el: 'Δείκτης ευεξίας',
    hr: 'Metrika dobrobiti',
  },
}

export default expandKeyedTranslations([reportLabels])
